import { useEffect, useRef } from 'react'
import { isFunction } from '../../framework/utils'
import type { ElementType, MutableRefObject, VFC } from 'react'

export type ReactBridgeOptions = {
  containerType?: ElementType
  containerProps?: Record<string, any>
}
export type ReactBridgeMountFn = (el: HTMLElement) => (el?: HTMLElement) => void
export type ReactBridgeProps = ReactBridgeOptions & {
  mount: ReactBridgeMountFn
}
export const ReactBridge: VFC<ReactBridgeProps> = ({ mount, containerType: Container = 'div', containerProps }) => {
  const ref = useRef() as MutableRefObject<HTMLElement>
  useEffect(() => {
    const container = ref.current
    const unmount = container && mount(container)
    return () => {
      if (isFunction(unmount)) {
        unmount(container)
      }
    }
  }, [mount])
  return <Container ref={ref} {...containerProps} />
}

export function createReactBridge<T = any>(mount: ReactBridgeMountFn, options?: ReactBridgeOptions) {
  const Bridge: VFC<T> = (props) => <ReactBridge {...props} {...options} mount={mount} />
  return Bridge
}

export function importReactBridgeComponent(importComponentMountFn: () => { default: ReactBridgeMountFn }, options?: ReactBridgeOptions) {
  return async () => {
    const mount = (await importComponentMountFn())?.default
    return { default: createReactBridge(mount, options) }
  }
}

export default ReactBridge
